'use client';

import { type ReactNode, useCallback } from 'react';
import { createContext } from 'use-context-selector';
import { useOnSignInChangedSubscription } from '@frameio/graphql-client/graphql/features/integrations';
import { getLink } from '@frameio/shared/routing';
import { useTrack, useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { useRedirectPath } from '../hooks';
import { useStoreAccessTokensToCookies } from '../hooks/useStoreAccessTokensToCookies';
import { getIntegrationsNewUserSignup } from '../utils/newUserStorage';

const SharedAuthContext = createContext<null>(null);

export const SharedAuthContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const redirectPath = useRedirectPath(
    getLink({
      page: 'workspaces',
    })
  );

  const storeAccessTokensToCookies = useStoreAccessTokensToCookies();
  const track = useTrack();
  const trackUnsafe = useTrackUnsafe();

  const handleSignedIn = useCallback(async () => {
    await storeAccessTokensToCookies();
    track('signed-in');

    if (getIntegrationsNewUserSignup()) {
      const url = new URL(redirectPath, window.location.origin);
      url.searchParams.set('signup', 'true');
      window.location.assign(`${url.pathname}${url.search}`);
      return;
    }

    window.location.assign(redirectPath);
  }, [storeAccessTokensToCookies, redirectPath, track]);

  const handleSignedOut = useCallback(() => {
    trackUnsafe('signed-out');
    window.location.assign(
      getLink({
        page: 'logout',
      })
    );
  }, [trackUnsafe]);

  useOnSignInChangedSubscription({
    context: {
      resolveWithUXP: true,
    },
    onData: async ({ data: subscriptionData }) => {
      if (typeof subscriptionData?.data?.signInChanged === 'undefined') return;

      if (subscriptionData?.data?.signInChanged) {
        await handleSignedIn();
      } else {
        handleSignedOut();
      }
    },
  });

  return (
    <SharedAuthContext.Provider value={null}>
      {children}
    </SharedAuthContext.Provider>
  );
};
